import { useEffect, useMemo, useState } from 'react'
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet'
import BaseMapSwitcher from './BaseMapSwitcher'
import { BASEMAPS } from '../utils/basemaps'
import { useBaseMap } from '../context/BaseMapContext'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'

type LatLng = [number, number]

export default function GPXViewerModal({ gpxText, onClose }: { gpxText: string; onClose: () => void }) {
  const data = useMemo(() => parseGPX(gpxText), [gpxText])
  const { baseId, setBaseId } = useBaseMap()
  const [focus, setFocus] = useState<LatLng | null>(null)
  const [sites, setSites] = useState<Array<{ id: number; name: string }>>([])
  const [siteId, setSiteId] = useState<number | ''>('')
  type AssetPos = { id: number; position_type: 'point' | 'polygon'; position_coords: [number, number] | [number, number][] }
  const [assetsByType, setAssetsByType] = useState<Record<string, AssetPos[]>>({})
  const effectiveSiteId = useMemo(() => {
    if (siteId) return siteId
    if (sites.length === 1) return sites[0].id
    return '' as ''
  }, [siteId, sites])

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const res = await fetchWithAuth('/api/sites')
        if (!res.ok) return
        const list = await res.json()
        if (!mounted) return
        setSites(list)
        if (Array.isArray(list) && list.length === 1) {
          setSiteId(list[0].id)
        }
      } catch {
        // ignore
      }
    })()
    return () => { mounted = false }
  }, [])

  // Fetch all assets for current site to allow existence checks
  useEffect(() => {
    let mounted = true
    async function load() {
      if (!effectiveSiteId) { setAssetsByType({}); return }
      try {
        const res = await fetchWithAuth(`/api/assets?site_id=${effectiveSiteId}`)
        if (!res.ok) return
        const data = await res.json() as { assets: Array<{ id: number; type: string; position_type: 'point' | 'polygon'; position_coords: [number, number] | [number, number][] }> }
        if (!mounted) return
        const map: Record<string, AssetPos[]> = {}
        data.assets.forEach(a => {
          if (!map[a.type]) map[a.type] = []
          map[a.type].push({ id: a.id, position_type: a.position_type, position_coords: a.position_coords })
        })
        setAssetsByType(map)
      } catch {
        // ignore
      }
    }
    load()
    return () => { mounted = false }
  }, [effectiveSiteId])

  const hasMatchingAsset = (w: { pos: LatLng; type?: string }) => {
    if (!effectiveSiteId || !w.type) return false
    const list = assetsByType[w.type] || []
    // Only compare with point assets for now
    const [lat, lon] = w.pos
    for (const a of list) {
      if (a.position_type !== 'point') continue
      const [aLon, aLat] = a.position_coords as [number, number] // stored as [lng, lat]
      if (distanceMeters(lat, lon, aLat, aLon) <= 15) {
        return true
      }
    }
    return false
  }

  const copyWaypointToAsset = async (w: { pos: LatLng; name?: string; type?: string }) => {
    if (!effectiveSiteId || !w.type) return
    try {
      const body = {
        name: w.name || (w.type.charAt(0).toUpperCase() + w.type.slice(1)),
        type: w.type,
        site_id: effectiveSiteId,
        position_type: 'point',
        position_coords: [w.pos[1], w.pos[0]], // [lng, lat]
        comment: 'Imported from GPX preview'
      }
      const res = await fetchWithAuth('/api/assets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (res.ok) {
        // Refresh the assets list to reflect the new asset
        const json = await res.json().catch(() => null)
        setAssetsByType(prev => {
          const list = prev[w.type!] ? [...prev[w.type!]] : []
          list.push({ id: json?.id || Date.now(), position_type: 'point', position_coords: [w.pos[1], w.pos[0]] })
          return { ...prev, [w.type!]: list }
        })
      } else {
        // no-op; UI could show error toast in future
      }
    } catch {
      // ignore
    }
  }
  return (
    <div className="modal-overlay">
      <div className="modal-container" style={{ width: '96vw', maxWidth: '1400px' }}>
        <div className="modal-header">
          <h3>GPX Preview</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="gpx-viewer-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: '1rem', alignItems: 'stretch' }}>
            <div style={{ height: '80vh', width: '100%' }}>
              <MapContainer center={data.center || [62, 15]} zoom={data.center ? 12 : 5} style={{ height: '100%', width: '100%', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
                <TileLayer key={baseId} url={BASEMAPS[baseId].url} attribution={BASEMAPS[baseId].attribution} {...(BASEMAPS[baseId].options || {})} />
                {data.bounds && <FitToBounds bounds={data.bounds} />}
                {focus && <FocusTo target={focus} />}
                {data.waypoints.map((w, i) => (
                  <Marker key={`wpt-${i}`} position={w.pos} icon={typeIcon(w.type, w.name)} />
                ))}
                {data.tracks.map((seg, i) => (
                  <Polyline key={`trk-${i}`} positions={seg} pathOptions={{ color: '#0070f3', weight: 3 }} />
                ))}
              </MapContainer>
            </div>
            <div className="gpx-sidebar" style={{ height: '80vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ color: '#374151', fontWeight: 600 }}>Waypoints</div>
                <BaseMapSwitcher value={baseId} onChange={setBaseId} />
              </div>
              {sites.length > 1 && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                  <label htmlFor="gpx-site-select" style={{ color: '#374151', fontWeight: 500 }}>Site</label>
                  <select id="gpx-site-select" className="form-select" value={siteId}
                          onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')}
                          style={{ flex: 1, minWidth: 0 }}>
                    <option value="">Select site…</option>
                    {sites.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                {data.waypoints.length === 0 ? (
                  <div style={{ padding: '0.75rem', color: '#6b7280' }}>No waypoints</div>
                ) : (
                  data.waypoints.map((w, i) => {
                    const exists = hasMatchingAsset(w)
                    return (
                      <div key={`li-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid #f3f4f6' }}>
                        <button className="btn" style={{ display: 'flex', flex: 1, justifyContent: 'flex-start', gap: '0.5rem', borderRadius: 0 }} onClick={() => setFocus(w.pos)}>
                          <span style={{ fontSize: 18 }}>{(typeIconEmoji(w.type))}</span>
                          <span style={{ color: '#111827' }}>{w.name || `Waypoint ${i+1}`}</span>
                          <span style={{ color: '#6b7280', marginLeft: 'auto', fontSize: 12 }}>{w.pos[0].toFixed(5)}, {w.pos[1].toFixed(5)}</span>
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', paddingRight: 8 }}>
                          {exists ? (
                            <span title="Asset exists at this location" aria-label="exists" style={{ fontSize: 16 }}>✅</span>
                          ) : (
                            <button className="btn btn-sm" title={effectiveSiteId ? 'Create asset from waypoint' : 'Select a site first'} disabled={!effectiveSiteId || !w.type} onClick={() => copyWaypointToAsset(w)}>Copy</button>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
                <div style={{ padding: '0.5rem 0.75rem', color: '#666' }}>
                  Segments: {data.tracks.length} · Track points: {data.tracks.reduce((a,s)=>a+s.length,0)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FitToBounds({ bounds }: { bounds: L.LatLngBoundsExpression }) {
  const map = useMap()
  useEffect(() => {
    try {
      map.fitBounds(bounds, { paddingTopLeft: L.point(30, 30), paddingBottomRight: L.point(30, 30) })
    } catch {}
  }, [bounds, map])
  return null
}

function FocusTo({ target }: { target: LatLng }) {
  const map = useMap()
  useEffect(() => {
    try { map.setView(target as L.LatLngExpression, Math.max(map.getZoom(), 14)) } catch {}
  }, [target, map])
  return null
}

function distanceMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

function typeIcon(type?: string, label?: string) {
  const iconMap: Record<string, string> = {
    hunting_border: '🟫', pass: '🚶', tower: '🗼', burrow: '🕳️', salt_lick: '🧂', food_place: '🥣', trap: '🪤',
    bridge: '🪵', parking: '🅿️', ford: '🌊'
  }
  const known = type && iconMap[type]
  const emoji = known || '⚠️'
  const title = known ? '' : (type ? `Unknown type: ${safe(type)}` : 'Unknown type')
  return L.divIcon({
    html: `
      <div class="gpx-marker" title="${title}">
        <div class="gpx-emoji">${emoji}</div>
        ${label ? `<div class="gpx-label">${safe(label)}</div>` : ''}
      </div>
    `,
    className: 'gpx-marker-icon',
    iconSize: [28, 40],
    iconAnchor: [14, 14]
  })
}

function typeIconEmoji(type?: string) {
  const iconMap: Record<string, string> = {
    hunting_border: '🟫', pass: '🚶', tower: '🗼', burrow: '🕳️', salt_lick: '🧂', food_place: '🥣', trap: '🪤',
    bridge: '🪵', parking: '🅿️', ford: '🌊'
  }
  return (type && iconMap[type]) || '⚠️'
}

function parseGPX(text: string): { waypoints: { pos: LatLng; name?: string; type?: string }[]; tracks: LatLng[][]; bounds?: L.LatLngBoundsExpression; center?: LatLng } {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(text, 'application/xml')
    if (doc.getElementsByTagName('parsererror').length) return { waypoints: [], tracks: [] }
    const waypoints: { pos: LatLng; name?: string; type?: string }[] = []
    doc.querySelectorAll('wpt').forEach(el => {
      const lat = Number(el.getAttribute('lat') || 'nan')
      const lon = Number(el.getAttribute('lon') || 'nan')
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        const name = el.querySelector('name')?.textContent || undefined
        const type = (el.querySelector('type')?.textContent || '').trim() || undefined
        waypoints.push({ pos: [lat, lon], name, type })
      }
    })
    const tracks: LatLng[][] = []
    doc.querySelectorAll('trkseg').forEach(seg => {
      const pts: LatLng[] = []
      seg.querySelectorAll('trkpt').forEach(pt => {
        const lat = Number(pt.getAttribute('lat') || 'nan')
        const lon = Number(pt.getAttribute('lon') || 'nan')
        if (Number.isFinite(lat) && Number.isFinite(lon)) pts.push([lat, lon])
      })
      if (pts.length) tracks.push(pts)
    })
    const all = [...waypoints.map(w=>w.pos), ...tracks.flat()]
    if (all.length) {
      let minLat =  90, minLon =  180, maxLat = -90, maxLon = -180
      all.forEach(([lat, lon]) => { if (lat<minLat)minLat=lat; if (lon<minLon)minLon=lon; if (lat>maxLat)maxLat=lat; if (lon>maxLon)maxLon=lon })
      return {
        waypoints,
        tracks,
        bounds: [[minLat, minLon],[maxLat, maxLon]],
        center: [(minLat+maxLat)/2, (minLon+maxLon)/2]
      }
    }
    return { waypoints, tracks }
  } catch {
    return { waypoints: [], tracks: [] }
  }
}

function safe(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' } as Record<string, string>)[c])
}
